import { inject, Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { ApiProviderService } from './api-provider.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TransportService } from './transport.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { TransportViewDTO } from '../models/transport-view-dto.model';
import { Transport } from '../models/transport.model';
import dayjs from 'dayjs';
import { createRequestOption, mapAsBody } from '../app/shared/util/request-util';
import { DATE_FORMAT } from '../models/constants/input.constants';
import { IBaryTableFetchService } from './i-bary-table-fetch-service';

type EntityType = TransportViewDTO;
type EntityResponseType = HttpResponse<EntityType>;
type EntityArrayResponseType = HttpResponse<EntityType[]>;

@Injectable({ providedIn: 'root' })
export class TransportExtendedService extends TransportService implements IBaryTableFetchService<EntityType> {
  readonly #apiProvider = inject(ApiProviderService);

  protected override resourceUrl = API_ENDPOINTS.TRANSPORT;

  public override find(id: number): Observable<Transport> {
    return this.#apiProvider
      .get<Transport>(`${this.resourceUrl}/${id}`)
      .pipe(mapAsBody)
      .pipe(map((res: any) => new Transport(res)));
  }

  override query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.#apiProvider.get<EntityType[]>(this.resourceUrl, options).pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  override delete(id: number): Observable<HttpResponse<{}>> {
    return this.#apiProvider.delete<{}>(`${this.resourceUrl}/${id}`);
  }

  // /!\ public URL
  public createEmpty(): Observable<Transport> {
    return this.#apiProvider
      .publicGet<EntityType>(this.resourceUrl)
      .pipe(mapAsBody)
      .pipe(map(transport => new Transport(transport)!));
  }

  // /!\ public URL
  public applyOffer(offerId: number, transport: Transport): Observable<Transport> {
    return this.#apiProvider
      .publicPost<EntityType>(`${this.resourceUrl}/applyOffer/${offerId}`, transport.serialize())
      .pipe(mapAsBody)
      .pipe(map(response => new Transport(response)));
  }

  preparePickupDate(exactTransport: EntityType): void {
    if (exactTransport.pickupDate) {
      const pickupDate = new Date(exactTransport.pickupDate.toDate());
      const hoursDiff = pickupDate.getHours() - pickupDate.getTimezoneOffset() / 60;
      const minutesDiff = (pickupDate.getHours() - pickupDate.getTimezoneOffset()) % 60;
      pickupDate.setHours(hoursDiff);
      pickupDate.setMinutes(minutesDiff);
      exactTransport.pickupDate = dayjs(pickupDate);
    } else {
      console.error('IllegalState: existing transport without pickupDate');
    }
  }

  prepareDeliveryDate(exactTransport: EntityType): void {
    if (exactTransport.deliveryDate) {
      const deliveryDate = new Date(exactTransport.deliveryDate.toDate());
      const hoursDiff = deliveryDate.getHours() - deliveryDate.getTimezoneOffset() / 60;
      const minutesDiff = (deliveryDate.getHours() - deliveryDate.getTimezoneOffset()) % 60;
      deliveryDate.setHours(hoursDiff);
      deliveryDate.setMinutes(minutesDiff);
      exactTransport.deliveryDate = dayjs(deliveryDate);
    } else {
      console.error('IllegalState: existing transport without deliveryDate');
    }
  }

  override convertDateFromClient(transport: EntityType): EntityType {
    return Object.assign({}, transport, {
      pickupDate: transport.pickupDate && dayjs(transport.pickupDate).isValid() ? dayjs(transport.pickupDate).format(DATE_FORMAT) : undefined,
      deliveryDate: transport.deliveryDate && dayjs(transport.deliveryDate).isValid() ? dayjs(transport.deliveryDate).format(DATE_FORMAT) : undefined,
      deliveryMaxDate: transport.deliveryMaxDate && dayjs(transport.deliveryMaxDate).isValid() ? dayjs(transport.deliveryMaxDate).format(DATE_FORMAT) : undefined,
      creationDate: transport.creationDate && dayjs(transport.creationDate).isValid() ? dayjs(transport.creationDate).toJSON() : undefined,
      quotationValidityDate: transport.quotationValidityDate?.isValid() ? transport.quotationValidityDate.toJSON() : undefined,
    });
  }

  override convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.pickupDate = res.body.pickupDate ? dayjs(res.body.pickupDate) : undefined;
      res.body.deliveryDate = res.body.deliveryDate ? dayjs(res.body.deliveryDate) : undefined;
      res.body.deliveryMaxDate = res.body.deliveryMaxDate ? dayjs(res.body.deliveryMaxDate) : undefined;
      res.body.creationDate = res.body.creationDate ? dayjs(res.body.creationDate) : undefined;
      res.body.quotationValidityDate = res.body.quotationValidityDate ? dayjs(res.body.quotationValidityDate) : undefined;
    }
    return res;
  }

  override convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((transport: EntityType) => {
        transport.pickupDate = transport.pickupDate ? dayjs(transport.pickupDate) : undefined;
        transport.deliveryDate = transport.deliveryDate ? dayjs(transport.deliveryDate) : undefined;
        transport.deliveryMaxDate = transport.deliveryMaxDate ? dayjs(transport.deliveryMaxDate) : undefined;
        transport.creationDate = transport.creationDate ? dayjs(transport.creationDate) : undefined;
        transport.quotationValidityDate = transport.quotationValidityDate ? dayjs(transport.quotationValidityDate) : undefined;
      });
    }
    return res;
  }

  override update(transport: Transport): Observable<HttpResponse<EntityType>> {
    return this.#apiProvider.put<EntityType>(`${this.resourceUrl}/${transport.id}`, transport);
  }

  override create(transport: Transport): Observable<HttpResponse<EntityType>> {
    return this.#apiProvider.post<EntityType>(`${this.resourceUrl}`, transport);
  }

  fillDataToShow(entities: EntityType[]): Observable<EntityType[]> {
    return of(entities);
  }

  public getByIdAndCustomerId(transportId: number, customerId: number) {
    return this.#apiProvider
      .get<Transport>(`${this.resourceUrl}/${transportId}${customerId ? '/customer/' + customerId : ''}`)
      .pipe(mapAsBody)
      .pipe(map((res: any) => new Transport(res)));
  }
}
